// UPDATED Investor.js
// MODERN INVESTOR DASHBOARD

import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import API from "../api";

export default function Investor() {

  const [investors, setInvestors] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {

    try {

      // FETCH INVESTORS
      const peopleRes = await axios.get(
        `${API}/api/people`
      );

      const investorList = peopleRes.data.filter(
        p => p.role === "investor"
      );

      setInvestors(investorList);

      // FETCH TRANSACTIONS
      const transactionRes = await axios.get(
        `${API}/api/transactions`,
        {
          params: {
            role: "admin"
          }
        }
      );

      setTransactions(transactionRes.data);

    } catch (err) {

      console.log(err);

    }
  };

  // GLOBAL STATS
  const globalStats = useMemo(() => {

    let totalLoan = 0;
    let totalPaid = 0;
    let totalProfit = 0;
    let dueCount = 0;

    transactions.forEach((t) => {

      const loan = Number(t.amount || 0);
      const paid = Number(t.paid || 0);

      totalLoan += loan;
      totalPaid += paid;
      totalProfit += (loan * 1.75) - loan;

      const balance = (loan * 1.75) - paid;

      if (
        t.lastDate &&
        new Date(t.lastDate) < new Date() &&
        balance > 0
      ) {
        dueCount++;
      }

    });

    return {
      totalLoan,
      totalPaid,
      totalProfit,
      dueCount
    };

  }, [transactions]);

  return (

    <div className="min-h-screen bg-[#f4f7fb] pb-28">

      {/* HEADER */}
      <div className="bg-white px-5 pt-6 pb-4 shadow-sm rounded-b-3xl">

        <h1 className="text-3xl font-bold text-slate-800">
          Investor Dashboard
        </h1>

        <p className="text-slate-500 mt-1">
          Manage investor performance & borrowers
        </p>

      </div>

      <div className="p-4">

        {/* TOP STATS */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">

          <div className="bg-white rounded-3xl p-5 shadow-sm">

            <p className="text-sm text-slate-500">
              Total Investment
            </p>

            <h2 className="text-2xl font-bold mt-2">
              ₹{globalStats.totalLoan}
            </h2>

          </div>

          <div className="bg-white rounded-3xl p-5 shadow-sm">

            <p className="text-sm text-slate-500">
              Total Collected
            </p>

            <h2 className="text-2xl font-bold text-green-600 mt-2">
              ₹{globalStats.totalPaid}
            </h2>

          </div>

          <div className="bg-white rounded-3xl p-5 shadow-sm">

            <p className="text-sm text-slate-500">
              Expected Profit
            </p>

            <h2 className="text-2xl font-bold text-blue-700 mt-2">
              ₹{globalStats.totalProfit}
            </h2>

          </div>

          <div className="bg-white rounded-3xl p-5 shadow-sm">

            <p className="text-sm text-slate-500">
              Due Alerts
            </p>

            <h2 className="text-2xl font-bold text-red-500 mt-2">
              {globalStats.dueCount}
            </h2>

          </div>

        </div>

        {/* SEARCH */}
        <div className="bg-white rounded-3xl shadow-sm p-4 mb-5">

          <input
            type="text"
            placeholder="Search investor..."
            value={search}
            onChange={(e) =>
              setSearch(e.target.value)
            }
            className="w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none"
          />

        </div>

        {/* INVESTORS */}
        <div className="space-y-5">

          {investors
            .filter((inv) =>
              inv.name
                .toLowerCase()
                .includes(search.toLowerCase())
            )
            .map((investor) => {

              // BORROWERS UNDER INVESTOR
              const investorTransactions =
                transactions.filter(
                  (t) =>
                    t.investor?._id === investor._id
                );

              // STATS
              let totalLoan = 0;
              let totalPaid = 0;

              investorTransactions.forEach((t) => {

                totalLoan += Number(t.amount || 0);
                totalPaid += Number(t.paid || 0);

              });

              const expectedReturn =
                totalLoan * 1.75;

              const profit =
                expectedReturn - totalLoan;

              const balance =
                expectedReturn - totalPaid;

              return (

                <div
                  key={investor._id}
                  className="bg-white rounded-3xl shadow-sm p-5"
                >

                  {/* TOP */}
                  <div className="flex justify-between items-start flex-wrap gap-4">

                    <div>

                      <h2 className="text-2xl font-bold text-slate-800">
                        {investor.name}
                      </h2>

                      <p className="text-slate-500 mt-1">
                        {investor.mobile}
                      </p>

                    </div>

                    <div className="bg-blue-100 text-blue-700 px-4 py-2 rounded-2xl font-semibold">
                      {investorTransactions.length} Borrowers
                    </div>

                  </div>

                  {/* STATS */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5">

                    <div className="bg-slate-50 rounded-2xl p-4">

                      <p className="text-sm text-slate-500">
                        Invested
                      </p>

                      <h3 className="font-bold text-xl mt-2">
                        ₹{totalLoan}
                      </h3>

                    </div>

                    <div className="bg-slate-50 rounded-2xl p-4">

                      <p className="text-sm text-slate-500">
                        Collected
                      </p>

                      <h3 className="font-bold text-xl text-green-600 mt-2">
                        ₹{totalPaid}
                      </h3>

                    </div>

                    <div className="bg-slate-50 rounded-2xl p-4">

                      <p className="text-sm text-slate-500">
                        Profit
                      </p>

                      <h3 className="font-bold text-xl text-blue-700 mt-2">
                        ₹{profit}
                      </h3>

                    </div>

                    <div className="bg-slate-50 rounded-2xl p-4">

                      <p className="text-sm text-slate-500">
                        Balance
                      </p>

                      <h3 className="font-bold text-xl text-red-500 mt-2">
                        ₹{balance}
                      </h3>

                    </div>

                  </div>

                  {/* BORROWERS */}
                  <div className="mt-6">

                    <h3 className="text-lg font-bold text-slate-800 mb-4">
                      Borrowers
                    </h3>

                    <div className="space-y-3">

                      {investorTransactions.length === 0 && (

                        <div className="bg-slate-50 rounded-2xl p-4 text-slate-500">
                          No borrowers assigned
                        </div>

                      )}

                      {investorTransactions.map((t) => {

                        const total =
                          Number(t.amount || 0) * 1.75;

                        const paid =
                          Number(t.paid || 0);

                        const balance =
                          total - paid;

                        const completed =
                          balance <= 0;

                        const overdue =
                          t.lastDate &&
                          new Date(t.lastDate) < new Date() &&
                          !completed;

                        return (

                          <div
                            key={t._id}
                            className="bg-slate-50 rounded-2xl p-4"
                          >

                            <div className="flex justify-between items-start flex-wrap gap-3">

                              <div>

                                <h4 className="font-bold text-slate-800 text-lg">
                                  {t.name}
                                </h4>

                                <p className="text-sm text-slate-500 mt-1">
                                  {t.mobile}
                                </p>

                              </div>

                              <div className="flex gap-2 flex-wrap">

                                {completed && (
                                  <span className="bg-green-100 text-green-700 text-xs px-3 py-1 rounded-full">
                                    Completed
                                  </span>
                                )}

                                {overdue && (
                                  <span className="bg-red-100 text-red-700 text-xs px-3 py-1 rounded-full">
                                    Overdue
                                  </span>
                                )}

                              </div>

                            </div>

                            {/* AMOUNTS */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">

                              <div className="bg-white rounded-2xl p-3">

                                <p className="text-xs text-slate-500">
                                  Loan
                                </p>

                                <h4 className="font-bold mt-1">
                                  ₹{t.amount}
                                </h4>

                              </div>

                              <div className="bg-white rounded-2xl p-3">

                                <p className="text-xs text-slate-500">
                                  Paid
                                </p>

                                <h4 className="font-bold text-green-600 mt-1">
                                  ₹{paid}
                                </h4>

                              </div>

                              <div className="bg-white rounded-2xl p-3">

                                <p className="text-xs text-slate-500">
                                  Balance
                                </p>

                                <h4 className="font-bold text-red-500 mt-1">
                                  ₹{balance}
                                </h4>

                              </div>

                              <div className="bg-white rounded-2xl p-3">

                                <p className="text-xs text-slate-500">
                                  Due Date
                                </p>

                                <h4 className="font-bold mt-1">
                                  {t.lastDate
                                    ? new Date(t.lastDate)
                                        .toLocaleDateString()
                                    : "N/A"}
                                </h4>

                              </div>

                            </div>

                          </div>

                        );

                      })}

                    </div>

                  </div>

                </div>

              );

            })}

        </div>

      </div>

    </div>
  );
}